import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Badge } from '../ui/badge';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Download, 
  SkipForward,
  Zap
} from 'lucide-react';

/**
 * ChainExecutionResult - Displays the results of an API test chain execution
 * 
 * This is a pure display component with no side effects.
 * All state management remains in the parent (APITesting.js)
 * 
 * @param {Object} result - The chain execution result object
 * @param {string} result.status - PASS | PARTIAL | FAIL
 * @param {number} result.total_steps - Total number of steps
 * @param {number} result.passed_steps - Number of passed steps
 * @param {number} result.failed_steps - Number of failed steps
 * @param {number} result.skipped_steps - Number of skipped steps
 * @param {number} result.duration_ms - Total execution time in ms
 * @param {Object} result.runtime_variables - Extracted variables from execution
 * @param {Array} result.step_results - Array of step execution results
 */
export const ChainExecutionResult = ({ result }) => {
  const { t } = useApiTestingLanguage();
  if (!result) return null;

  return (
    <div className="space-y-4" data-testid="chain-execution-result">
      {/* Status Summary */}
      <div className={`p-4 rounded-lg ${
        result.status === 'PASS' ? 'bg-green-50 border border-green-200' :
        result.status === 'PARTIAL' ? 'bg-yellow-50 border border-yellow-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          {result.status === 'PASS' ? (
            <CheckCircle className="h-6 w-6 text-green-600" />
          ) : result.status === 'PARTIAL' ? (
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
          ) : (
            <XCircle className="h-6 w-6 text-red-600" />
          )}
          <span className="font-semibold text-lg">
            {result.status === 'PASS' ? t("All Steps Passed") :
             result.status === 'PARTIAL' ? t("Partially Completed") :
             t("Chain Failed")}
          </span>
        </div>
        <div className="grid grid-cols-5 gap-3 text-sm">
          <div>
            <span className="text-gray-500">{t("Total:")}</span>
            <span className="ml-1 font-medium">{result.total_steps}</span>
          </div>
          <div>
            <span className="text-green-600">{t("Passed:")}</span>
            <span className="ml-1 font-medium">{result.passed_steps}</span>
          </div>
          <div>
            <span className="text-red-600">{t("Failed:")}</span>
            <span className="ml-1 font-medium">{result.failed_steps}</span>
          </div>
          <div>
            <span className="text-amber-600">{t("Skipped:")}</span>
            <span className="ml-1 font-medium">{result.skipped_steps || 0}</span>
          </div>
          <div>
            <span className="text-gray-500">{t("Duration:")}</span>
            <span className="ml-1 font-medium">{result.duration_ms}ms</span>
          </div>
        </div>
      </div>

      {/* Runtime Variables */}
      {Object.keys(result.runtime_variables || {}).length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
            <Download className="h-4 w-4" />
            {t("Extracted Variables (Runtime Context)")}
          </h4>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {Object.entries(result.runtime_variables).map(([key, value]) => (
              <div key={key} className="bg-white p-2 rounded border">
                <span className="font-mono text-green-700">{`{{${key}}}`}</span>
                <span className="text-gray-500 ml-2 truncate block text-xs">
                  = {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step Results */}
      <div>
        <h4 className="font-medium mb-2">{t("Step-by-Step Results:")}</h4>
        <div className="space-y-2">
          {result.step_results?.map((step, idx) => (
            <StepResultCard key={idx} step={step} />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * StepResultCard - Displays a single step's execution result
 * Internal component, not exported
 */
const StepResultCard = ({ step }) => {
  const { t } = useApiTestingLanguage();
  const getStatusStyles = () => {
    if (step.status === 'PASS') return 'border-green-200 bg-green-50';
    if (step.status === 'SKIPPED' || step.status === 'CONDITION_NOT_MET') return 'border-amber-200 bg-amber-50';
    return 'border-red-200 bg-red-50';
  };

  const getStatusIcon = () => {
    if (step.status === 'PASS') {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    }
    if (step.status === 'SKIPPED' || step.status === 'CONDITION_NOT_MET') {
      return <SkipForward className="h-5 w-5 text-amber-500" />;
    }
    return <XCircle className="h-5 w-5 text-red-600" />;
  };

  return (
    <div className={`border rounded-lg p-3 ${getStatusStyles()}`} data-testid={`step-result-${step.step_order}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
            {step.step_order}
          </span>
          {getStatusIcon()}
          <span className="font-medium">{step.test_name || t("Unknown Test")}</span>
          <Badge variant="outline" className={
            step.status === 'CONDITION_NOT_MET' ? 'bg-amber-100 text-amber-700 border-amber-300' : ''
          }>
            {step.status === 'CONDITION_NOT_MET' ? t("Condition Not Met") : t(step.status)}
          </Badge>
        </div>
        <div className="text-sm text-gray-500">
          {step.response_status_code && `HTTP ${step.response_status_code}`}
          {step.response_time && ` • ${Math.round(step.response_time)}ms`}
        </div>
      </div>
      
      {/* Condition evaluation result */}
      {step.condition && (
        <div className={`mt-2 ml-8 text-xs flex items-center gap-1 ${
          step.condition_result === false ? 'text-amber-600' : 'text-green-600'
        }`}>
          <Zap className="h-3 w-3" />
          <span>{t("Condition:")} <code className="bg-white px-1 rounded">{step.condition}</code></span>
          <span>→ {step.condition_result === false ? 'false (skipped)' : 'true'}</span>
        </div>
      )}
      
      {step.condition_error && (
        <p className="text-xs text-red-500 mt-1 ml-8">
          {t("Condition error:")} {step.condition_error}
        </p>
      )}
      
      {step.skipped_reason && (
        <p className="text-sm text-gray-500 mt-2 ml-8">{step.skipped_reason}</p>
      )}
      
      {step.error_message && (
        <p className="text-sm text-red-600 mt-2 ml-8">{step.error_message}</p>
      )}
      
      {Object.keys(step.extracted_variables || {}).length > 0 && (
        <div className="mt-2 ml-8 text-sm">
          <span className="text-green-600">{t("Extracted:")} </span>
          {Object.keys(step.extracted_variables).join(', ')}
        </div>
      )}
    </div>
  );
};

export default ChainExecutionResult;
